/**
 * The small components everything else is built from.
 *
 * Each exists because a Design Bible rule is easy to break by hand: identifiers
 * get ellipsized, chips lose their glyph and become colour-only, money gets a
 * transition, a price gets recomputed in a view. Centralising them makes the
 * rule the default rather than a review item.
 */

import type { ReactNode } from "react";
import { formatTimestamp, groupIdentifier } from "../lib/format.js";
import { formatUsdMinor } from "../lib/money.js";

export type Tone = "ok" | "stop" | "accent" | "neutral";

/**
 * 6.6: every state carries icon, text, and colour, in that priority. The
 * glyphs come from the Design Bible's own wireframe vocabulary, so a monochrome
 * screenshot stays interpretable and no emoji is used as a status channel.
 */
const GLYPHS: Readonly<Record<Tone, string>> = {
  ok: "+",
  stop: "!",
  accent: ">",
  neutral: "·"
};

export function StatusChip({ tone, label }: { tone: Tone; label: string }): ReactNode {
  return (
    <span className={`chip chip--${tone}`}>
      <span aria-hidden="true" className="chip-glyph">
        {GLYPHS[tone]}
      </span>
      {label}
    </span>
  );
}

/**
 * 5.3: grouped in fours with a non-selectable separator, so a click-drag copy
 * yields the raw value. Never truncated, never ellipsized — it wraps instead.
 * A collector pasting a certification number into the grader's own lookup is
 * the most consequential copy on this site.
 */
export function Identifier({
  value,
  size = "s",
  label
}: {
  value: string;
  size?: "m" | "s" | "xs";
  label?: string;
}): ReactNode {
  const groups = groupIdentifier(value);
  return (
    <span
      className={size === "s" ? "t-id" : `t-id t-id--${size}`}
      {...(label === undefined ? {} : { "aria-label": `${label} ${value}` })}
    >
      {groups.map((group, index) => (
        <span key={`${group}-${String(index)}`}>
          {index > 0 ? (
            <span aria-hidden="true" className="t-id-gap">
              {" "}
            </span>
          ) : null}
          {group}
        </span>
      ))}
    </span>
  );
}

/**
 * 8.3: money never animates and is never recomputed by a view. `Money` takes
 * an integer minor amount that came from core and renders it with tabular
 * figures. There is no `Money` that takes two numbers and adds them.
 */
export function Money({ minor, size = "m" }: { minor: number; size?: "m" | "s" }): ReactNode {
  return <span className={size === "m" ? "t-id t-id--m money" : "t-id money"}>{formatUsdMinor(minor)}</span>;
}

/** A fact core did not publish. Never rendered as `0`, `—`, or an empty cell. */
export function NotPublished({ what }: { what: string }): ReactNode {
  return <span className="t-body-s t-secondary">Not published — {what}</span>;
}

export function DataPlate({
  label,
  children,
  note
}: {
  label: string;
  children: ReactNode;
  note?: string | undefined;
}): ReactNode {
  return (
    <div className="plate">
      <span className="t-label">{label}</span>
      <span className="plate-value">{children}</span>
      {note === undefined ? null : <span className="plate-note t-body-s t-secondary">{note}</span>}
    </div>
  );
}

export function PlateGrid({ children, columns }: { children: ReactNode; columns?: 2 | 3 }): ReactNode {
  return <div className={columns === 3 ? "plates plates--three" : "plates"}>{children}</div>;
}

/** 10.3: local time with an explicit zone label. Never a bare local string. */
export function Timestamp({ value, what }: { value: string | null; what: string }): ReactNode {
  const formatted = formatTimestamp(value);
  if (formatted === null) return <NotPublished what={what} />;
  if (!formatted.valid) return <span className="t-id">{formatted.local}</span>;
  return (
    <span>
      <span className="t-id">{formatted.local}</span>{" "}
      <span className="t-body-s t-secondary">{formatted.zone}</span>
    </span>
  );
}

/**
 * 11.5 / 18.2: catalog copy, condition statements, and anything else that
 * originated outside this system render as inert quoted content — never as
 * rich text, never as anything that could be mistaken for a system message.
 * On a public surface this is also the anti-injection rule: nothing here is
 * ever interpreted, only displayed.
 */
export function Quoted({ text, label }: { text: string; label: string }): ReactNode {
  return (
    <figure className="quote-figure">
      <figcaption className="t-label">{label}</figcaption>
      <blockquote className="quote">{text}</blockquote>
    </figure>
  );
}

export function Section({
  title,
  action,
  children,
  headingLevel = 2
}: {
  title: string;
  action?: ReactNode;
  children: ReactNode;
  headingLevel?: 2 | 3;
}): ReactNode {
  const Heading = headingLevel === 2 ? "h2" : "h3";
  return (
    <section className="section">
      <div className="section-head">
        <Heading className="section-title t-display-xs">{title}</Heading>
        {action}
      </div>
      {children}
    </section>
  );
}

/**
 * 11.1: the pending state replaces the label with the verb in progress, keeps
 * the button's width, and marks it busy. Never a bare spinner replacing a
 * label, and never a button whose disabled state has no stated reason.
 */
export function ActionButton({
  variant = "primary",
  pending = false,
  pendingLabel,
  disabledReason,
  onClick,
  children,
  type = "button",
  id
}: {
  variant?: "primary" | "secondary" | "quiet";
  pending?: boolean;
  pendingLabel?: string;
  disabledReason?: string | undefined;
  onClick?: () => void;
  children: ReactNode;
  type?: "button" | "submit";
  id?: string;
}): ReactNode {
  const blocked = disabledReason !== undefined || pending;
  const describedBy = disabledReason === undefined || id === undefined ? undefined : `${id}-reason`;
  return (
    <>
      <button
        aria-busy={pending}
        className={`btn btn--${variant}`}
        disabled={blocked}
        {...(describedBy === undefined ? {} : { "aria-describedby": describedBy })}
        {...(id === undefined ? {} : { id })}
        {...(onClick === undefined ? {} : { onClick })}
        type={type}
      >
        {pending && pendingLabel !== undefined ? pendingLabel : children}
      </button>
      {disabledReason === undefined ? null : (
        <p className="t-body-s t-secondary" {...(describedBy === undefined ? {} : { id: describedBy })}>
          {disabledReason}
        </p>
      )}
    </>
  );
}
